import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { breakfastEventsKey } from "../queryKeys";
import type { BreakfastEventWithAttendees } from "../types";

/**
 * Fetch every breakfast event visible to `userId` in the inclusive date
 * window `[startDate, endDate]`.
 *
 * ## Visibility rule
 *
 * An event only shows on a user's calendar when ONE of the following is
 * true:
 *
 *   1. They are the host.                       (ev.host_id === me)
 *   2. They are an attendee AND have explicitly accepted — mirrored in
 *      the DB as `status === 'confirmed'`.
 *   3. They were an attendee who got removed post-accept — mirrored in
 *      the DB as `status === 'removed'`. Outlook-style UX keeps these
 *      events on the calendar (rendered with the "cancelled" visual
 *      state) until the user explicitly taps "Remove from calendar" on
 *      the matching bell notification.
 *
 * Everything else — including still-pending invites — is hidden.
 * Rationale: a pending invite must not claim real estate on someone
 * else's calendar the same way an accepted meeting does. That matches
 * the Outlook / Google / Apple Calendar semantic: invites live in the
 * notifications inbox until accepted. Previously we only excluded
 * `declined` / `dismissed`, which meant every pending invite cluttered
 * guests' calendars immediately after the host hit Save.
 *
 * (Note: the DB enum uses `'confirmed'`, not `'accepted'` — same
 * concept, different spelling.)
 */
/**
 * Hard ceiling on the number of breakfast events returned per query.
 *
 * Audit W2: the query pulled an unbounded slice of `breakfast_events` within
 * a date window with deeply nested joins (attendees + profiles, potluck
 * items + claimer profiles). A heavy workspace or a wide window could
 * balloon the payload and stall the browser. 500 events well exceeds any
 * realistic 90-day window for a single user but protects the client from
 * runaway datasets.
 */
const BREAKFAST_EVENTS_HARD_LIMIT = 500;

export function useBreakfastEvents(
  startDate: string,
  endDate: string,
  userId?: string,
) {
  return useQuery({
    queryKey: breakfastEventsKey(userId, startDate, endDate),
    enabled: !!userId,
    retry: false, // Don't loop on RLS errors — surface them immediately.
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("breakfast_events")
        .select(`
          *,
          attendees:breakfast_attendees(
            id, event_id, user_id, status, bring_item,
            profiles(first_name, last_name, avatar_url, email)
          ),
          potluck_items(
            id, event_id, item_name, claimed_by, created_at,
            claimer:profiles(first_name, last_name, email)
          )
        `)
        .gte("event_date", startDate)
        .lte("event_date", endDate)
        .order("event_date", { ascending: true })
        .order("event_time", { ascending: true })
        // Deterministic order for the nested `potluck_items` array. Without
        // this, PostgreSQL returns rows in physical heap order which shifts
        // after an UPDATE on `claimed_by` (HOT updates can relocate tuples),
        // which is what was causing items to "jump" positions when other
        // users claimed them in realtime. Sorting by `created_at` keeps the
        // ledger strictly in creation order on every refetch.
        .order("created_at", {
          ascending: true,
          referencedTable: "potluck_items",
        })
        .limit(BREAKFAST_EVENTS_HARD_LIMIT);

      if (error) {
        console.error("[useBreakfastEvents]", error.message, error);
        throw error;
      }

      const parsed = (data as unknown) as BreakfastEventWithAttendees[];
      if (!userId) return parsed;

      return parsed.filter((ev) => {
        if (ev.host_id === userId) {
          // While an event is active, the host always sees it. After the
          // event is `cancelled`, the host "Remove from calendar" path
          // hard-deletes their `breakfast_attendees` row — without this
          // check they would still match on `host_id` alone and the card
          // would never leave the list (e.g. Circles → Upcoming Events).
          if (ev.status === "cancelled") {
            return (ev.attendees ?? []).some((a) => a.user_id === userId);
          }
          return true;
        }
        const myRow = ev.attendees?.find((a) => a.user_id === userId);
        // `confirmed` — normal accepted attendance.
        // `removed` — host revoked this attendee post-accept; keep the
        // event visible with the cancelled state so the user can
        // explicitly dismiss it via the bell (see NotifRow
        // EVENT_CANCELLED handler).
        return myRow?.status === "confirmed" || myRow?.status === "removed";
      });
    },
  });
}
